import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import StudentLayout from '@/components/layouts/StudentLayout';
import { 
  Globe, ArrowLeft, Trophy, Star, 
  RotateCcw, Sun, Moon, Rocket, Orbit
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const SolarSystemGame = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [gameState, setGameState] = useState('ready');
  const [currentPlanet, setCurrentPlanet] = useState(0);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [showResult, setShowResult] = useState(false);
  const [score, setScore] = useState(0);
  const [correctAnswers, setCorrectAnswers] = useState(0);
  const [orbitAngle, setOrbitAngle] = useState(0);

  const planets = [
    {
      id: 1,
      name: 'Mercury',
      emoji: '⚫',
      color: 'from-gray-400 to-gray-600',
      distance: '57.9 million km from Sun',
      facts: ['Smallest planet in our solar system', 'Has no moons', 'One day on Mercury = 59 Earth days', 'Surface temperature varies from -180°C to 430°C'],
      question: 'Why does Mercury have such extreme temperatures?',
      options: ['It has a thick atmosphere', 'It has no atmosphere to retain heat', 'It spins very fast', 'It is made of ice'],
      correctAnswer: 1,
      explanation: 'Mercury has almost no atmosphere, so it cannot trap heat. The side facing the Sun gets extremely hot, while the dark side gets extremely cold!',
      points: 20
    },
    {
      id: 2,
      name: 'Venus',
      emoji: '🟡',
      color: 'from-yellow-400 to-orange-500',
      distance: '108.2 million km from Sun',
      facts: ['Hottest planet (even hotter than Mercury!)', 'Rotates backwards compared to Earth', 'A day on Venus is longer than its year', 'Called Earth\'s "twin" due to similar size'],
      question: 'Why is Venus hotter than Mercury even though it\'s further from the Sun?',
      options: ['It is closer to the Sun', 'Its thick atmosphere traps heat (greenhouse effect)', 'It has volcanoes', 'It spins faster'],
      correctAnswer: 1,
      explanation: 'Venus has a thick atmosphere of carbon dioxide that traps heat like a greenhouse. This is called the runaway greenhouse effect!',
      points: 25
    },
    {
      id: 3,
      name: 'Earth',
      emoji: '🌍',
      color: 'from-blue-400 to-green-500',
      distance: '149.6 million km from Sun',
      facts: ['Only planet known to have life', 'Has one moon', '71% of surface is water', 'Takes 365.25 days to orbit the Sun'],
      question: 'What makes Earth able to support life?',
      options: ['It has rings', 'It has the right temperature, water, and atmosphere', 'It is the largest planet', 'It has many moons'],
      correctAnswer: 1,
      explanation: 'Earth is in the "Goldilocks zone" - not too hot, not too cold. It has liquid water, an oxygen-rich atmosphere, and a magnetic field that protects us from solar radiation!',
      points: 20
    },
    {
      id: 4,
      name: 'Mars',
      emoji: '🔴',
      color: 'from-red-400 to-red-600',
      distance: '227.9 million km from Sun',
      facts: ['Called the "Red Planet"', 'Has the largest volcano in the solar system (Olympus Mons)', 'Has 2 small moons: Phobos and Deimos', 'Scientists are looking for signs of past life here'],
      question: 'Why is Mars red?',
      options: ['It is very hot', 'Iron oxide (rust) in the soil', 'It is covered in lava', 'It reflects the Sun'],
      correctAnswer: 1,
      explanation: 'Mars is red because its surface contains iron oxide - the same compound that makes rust red on Earth!',
      points: 20
    },
    {
      id: 5,
      name: 'Jupiter',
      emoji: '🟤',
      color: 'from-orange-300 to-amber-600',
      distance: '778.5 million km from Sun',
      facts: ['Largest planet - 1,300 Earths could fit inside!', 'Has at least 95 moons', 'The Great Red Spot is a giant storm', 'Made mostly of hydrogen and helium gas'],
      question: 'What is Jupiter\'s Great Red Spot?',
      options: ['A volcano', 'A giant storm that has lasted hundreds of years', 'A crater', 'A moon'],
      correctAnswer: 1,
      explanation: 'The Great Red Spot is a massive storm larger than Earth that has been raging for at least 400 years!',
      points: 25
    },
    {
      id: 6,
      name: 'Saturn',
      emoji: '🪐',
      color: 'from-yellow-200 to-yellow-500',
      distance: '1.4 billion km from Sun',
      facts: ['Famous for its beautiful rings', 'Has 146 known moons (most of any planet!)', 'So light it could float in water', 'Rings are made of ice and rock'],
      question: 'What are Saturn\'s rings made of?',
      options: ['Gas clouds', 'Solid rock', 'Billions of pieces of ice and rock', 'Light reflections'],
      correctAnswer: 2,
      explanation: 'Saturn\'s rings are made of billions of chunks of ice and rock, ranging from tiny grains to house-sized boulders!',
      points: 25
    },
    {
      id: 7,
      name: 'Uranus',
      emoji: '🔵',
      color: 'from-cyan-300 to-blue-400',
      distance: '2.9 billion km from Sun',
      facts: ['Rotates on its side (tilted 98 degrees!)', 'Has 27 known moons', 'Appears blue-green due to methane', 'Coldest planetary atmosphere (-224°C)'],
      question: 'Why is Uranus tilted on its side?',
      options: ['It was always like that', 'It was probably hit by a large object long ago', 'The Sun\'s gravity pulled it', 'It has too many moons'],
      correctAnswer: 1,
      explanation: 'Scientists believe a massive collision with an Earth-sized object billions of years ago knocked Uranus onto its side!',
      points: 30
    },
    {
      id: 8,
      name: 'Neptune',
      emoji: '🔷',
      color: 'from-blue-500 to-indigo-600',
      distance: '4.5 billion km from Sun',
      facts: ['Has the strongest winds in the solar system (2,000 km/h)', 'Takes 165 Earth years to orbit the Sun', 'Has 16 known moons', 'Was discovered using mathematics before being seen'],
      question: 'How was Neptune discovered?',
      options: ['Seen through a telescope by accident', 'Predicted by math before being observed', 'Visited by spacecraft first', 'Found on old maps'],
      correctAnswer: 1,
      explanation: 'Neptune was the first planet found using mathematical predictions! Scientists noticed Uranus wasn\'t moving as expected and calculated where another planet must be.',
      points: 30
    }
  ];

  const currentPlanetData = planets[currentPlanet];

  // Orbit animation
  useEffect(() => {
    if (gameState === 'playing') {
      const interval = setInterval(() => {
        setOrbitAngle(a => (a + 1) % 360);
      }, 50);
      return () => clearInterval(interval);
    }
  }, [gameState]);

  const startGame = () => {
    setGameState('playing');
    setCurrentPlanet(0);
    setSelectedAnswer(null);
    setShowResult(false);
    setScore(0);
    setCorrectAnswers(0);
  };

  const handleAnswer = (index) => {
    if (showResult) return;
    setSelectedAnswer(index);
  };

  const checkAnswer = () => {
    if (selectedAnswer === null) {
      toast.error('Select an answer first!');
      return;
    }

    setShowResult(true);
    if (selectedAnswer === currentPlanetData.correctAnswer) {
      setScore(s => s + currentPlanetData.points);
      setCorrectAnswers(c => c + 1);
      toast.success('Correct! Great space knowledge!');
    } else {
      toast.error('Not quite right. Learn more below!');
    }
  };

  const nextPlanet = () => {
    if (currentPlanet < planets.length - 1) {
      setCurrentPlanet(c => c + 1);
      setSelectedAnswer(null);
      setShowResult(false);
    } else {
      endGame();
    }
  };

  const endGame = async () => {
    setGameState('finished');
    
    try {
      await api.post('/games/complete', {
        game_id: 'solar-system',
        score: score,
        metadata: { 
          planets_explored: correctAnswers,
          total_planets: planets.length
        }
      });
      toast.success(`🚀 You earned ${score} points!`);
    } catch (error) {
      console.error('Error saving game:', error);
    }
  };

  return (
    <StudentLayout>
      <div data-testid="solar-system-game" className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={() => navigate('/student/games')}
            className="flex items-center gap-2 text-slate-600 hover:text-purple-600 font-medium"
          >
            <ArrowLeft className="w-5 h-5" />
            Back to Games
          </button>
        </div>

        {/* Game Header */}
        <div className="student-card bg-slate-900 border-indigo-500 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-indigo-600 rounded-2xl flex items-center justify-center">
                <Globe className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-bold text-white">Solar System Explorer</h1>
                <p className="text-indigo-300">
                  {gameState === 'playing' 
                    ? `Planet ${currentPlanet + 1} of ${planets.length}` 
                    : 'Explore the planets!'}
                </p>
              </div>
            </div>
            {gameState === 'playing' && (
              <div className="flex items-center gap-2 bg-indigo-900 px-4 py-2 rounded-full">
                <Star className="w-5 h-5 text-amber-400" />
                <span className="font-heading text-xl font-bold text-white">{score}</span>
              </div>
            )}
          </div>
        </div>

        {/* Ready Screen */}
        {gameState === 'ready' && (
          <div className="student-card bg-gradient-to-b from-slate-900 to-indigo-900 text-center py-12">
            <div className="relative w-32 h-32 mx-auto mb-6">
              <div className="absolute inset-0 flex items-center justify-center">
                <Sun className="w-12 h-12 text-yellow-400" />
              </div>
              <div className="absolute inset-0 animate-spin" style={{ animationDuration: '10s' }}>
                <div className="absolute top-0 left-1/2 -translate-x-1/2">
                  <span className="text-2xl">🌍</span>
                </div>
              </div>
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-4">Solar System Explorer</h2>
            <p className="text-lg text-indigo-200 mb-8">
              Journey through our solar system!<br/>
              Visit each planet and learn amazing facts.
            </p>
            <div className="flex justify-center gap-6 mb-8">
              <div className="text-center">
                <Rocket className="w-10 h-10 text-indigo-400 mx-auto mb-2" />
                <p className="text-sm text-indigo-300">Visit planets</p>
              </div>
              <div className="text-center">
                <Orbit className="w-10 h-10 text-purple-400 mx-auto mb-2" />
                <p className="text-sm text-indigo-300">Learn orbits</p>
              </div>
              <div className="text-center">
                <Moon className="w-10 h-10 text-slate-400 mx-auto mb-2" />
                <p className="text-sm text-indigo-300">Discover moons</p>
              </div>
            </div>
            <Button onClick={startGame} className="bg-indigo-500 hover:bg-indigo-600 text-white text-lg px-10 py-6">
              Launch Mission
              <Rocket className="w-6 h-6 ml-2" />
            </Button>
          </div>
        )}

        {/* Playing Screen */}
        {gameState === 'playing' && currentPlanetData && (
          <div className="student-card bg-gradient-to-b from-slate-900 to-slate-800">
            {/* Planet Display */}
            <div className={`rounded-xl p-6 mb-6 bg-gradient-to-br ${currentPlanetData.color}`}>
              <div className="flex items-center gap-4">
                <span className="text-6xl">{currentPlanetData.emoji}</span>
                <div>
                  <h2 className="font-heading text-2xl font-bold text-white">{currentPlanetData.name}</h2>
                  <p className="text-white/80">{currentPlanetData.distance}</p>
                </div>
              </div>
            </div>

            {/* Fun Facts */}
            <div className="bg-slate-700 rounded-xl p-4 mb-6">
              <p className="font-semibold text-indigo-300 mb-2">🌟 Amazing Facts:</p>
              <ul className="text-sm text-slate-300 space-y-1">
                {currentPlanetData.facts.map((fact, i) => (
                  <li key={i}>• {fact}</li>
                ))}
              </ul>
            </div>

            {/* Question */}
            <div className="bg-indigo-900 rounded-xl p-4 mb-4">
              <p className="font-semibold text-white">{currentPlanetData.question}</p>
            </div>

            {/* Options */}
            <div className="grid grid-cols-1 gap-3 mb-4">
              {currentPlanetData.options.map((option, idx) => (
                <button
                  key={idx}
                  onClick={() => handleAnswer(idx)}
                  disabled={showResult}
                  className={`p-4 rounded-xl border-2 font-semibold transition-all text-left ${
                    showResult
                      ? idx === currentPlanetData.correctAnswer
                        ? 'bg-emerald-500/20 border-emerald-500 text-emerald-300'
                        : idx === selectedAnswer
                          ? 'bg-red-500/20 border-red-500 text-red-300'
                          : 'bg-slate-700 border-slate-600 text-slate-400'
                      : selectedAnswer === idx
                        ? 'bg-indigo-500 border-indigo-400 text-white'
                        : 'bg-slate-700 hover:bg-slate-600 border-slate-600 text-slate-200'
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>

            {/* Check/Next Button */}
            {!showResult ? (
              <Button 
                onClick={checkAnswer}
                disabled={selectedAnswer === null}
                className="w-full bg-indigo-500 hover:bg-indigo-600 text-white"
              >
                Check Answer
              </Button>
            ) : (
              <div>
                <div className={`rounded-xl p-4 mb-4 ${
                  selectedAnswer === currentPlanetData.correctAnswer ? 'bg-emerald-900/50' : 'bg-amber-900/50'
                }`}>
                  <p className={`font-semibold ${
                    selectedAnswer === currentPlanetData.correctAnswer ? 'text-emerald-300' : 'text-amber-300'
                  }`}>
                    {currentPlanetData.explanation}
                  </p>
                  {selectedAnswer === currentPlanetData.correctAnswer && (
                    <p className="mt-2 font-bold text-emerald-400">+{currentPlanetData.points} points!</p>
                  )}
                </div>
                <Button onClick={nextPlanet} className="w-full bg-indigo-500 hover:bg-indigo-600 text-white">
                  {currentPlanet < planets.length - 1 ? `Travel to ${planets[currentPlanet + 1].name}` : 'Complete Mission'}
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Finished Screen */}
        {gameState === 'finished' && (
          <div className="student-card bg-gradient-to-b from-slate-900 to-indigo-900 text-center py-12">
            <div className="w-20 h-20 bg-amber-400 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
              <Trophy className="w-12 h-12 text-white" />
            </div>
            <h2 className="font-heading text-3xl font-bold text-white mb-2">Mission Complete!</h2>
            <p className="text-xl text-indigo-200 mb-4">
              You explored {correctAnswers} out of {planets.length} planets successfully!
            </p>
            
            <div className="flex items-center justify-center gap-2 mb-8">
              <Star className="w-8 h-8 text-amber-400 fill-amber-400" />
              <span className="font-heading text-4xl font-bold text-amber-400">+{score}</span>
              <span className="text-indigo-200 text-xl">points</span>
            </div>
            
            <div className="flex gap-4 justify-center">
              <Button variant="outline" onClick={startGame} className="border-indigo-400 text-indigo-300 hover:bg-indigo-900">
                <RotateCcw className="w-4 h-4 mr-2" />
                Play Again
              </Button>
              <Button onClick={() => navigate('/student/games')} className="bg-indigo-500 hover:bg-indigo-600 text-white">
                More Games
              </Button>
            </div>
          </div>
        )}
      </div>
    </StudentLayout>
  );
};

export default SolarSystemGame;
